import { useEffect, useSyncExternalStore } from "react"

type Theme = "light" | "dark" | "system"

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system"
  return (localStorage.getItem("theme") as Theme) || "system"
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  const resolved = theme === "system" ? getSystemTheme() : theme

  if (resolved === "dark") {
    root.classList.add("dark")
  } else {
    root.classList.remove("dark")
  }
}

/*
 * One theme for the whole page. Every useTheme() reads this store, so the
 * top-bar toggle and the avatar menu's Theme submenu can't disagree (each
 * caller used to keep its own useState copy, and a stale copy could
 * re-apply the old theme). Another tab changing it arrives via "storage".
 */
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === "theme") listener()
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", onStorage)
  }
}

export function useTheme() {
  const theme = useSyncExternalStore<Theme>(subscribe, getStoredTheme, () => "system")

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Listen for system theme changes when in "system" mode
  useEffect(() => {
    if (theme !== "system") return

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = () => applyTheme("system")

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem("theme", newTheme)
    listeners.forEach((listener) => listener())
  }

  const toggleTheme = () => {
    // Cycle: light -> dark -> system -> light
    const next: Record<Theme, Theme> = {
      light: "dark",
      dark: "system",
      system: "light",
    }
    setTheme(next[theme])
  }

  return {
    theme,
    setTheme,
    toggleTheme,
    resolvedTheme: theme === "system" ? getSystemTheme() : theme,
  }
}
